import React, { useState, useEffect, useRef } from 'react';
import { Tag, CheckCircle, RotateCcw } from 'lucide-react';
import { PracticeCard } from './PracticeCard';
import { LeafCard } from './CategoryPracticePhrasesSession';
import { trackPracticeStarted, trackPracticeCompleted, trackPracticeAbandoned } from '../utils/analytics';

interface CategoryPracticeSessionProps {
  name: string;
  initialLeafCards: LeafCard[];
  onClose: () => void;
  isInstanceOwner?: boolean;
  onDelete?: (id: number) => void;
  onMove?: (itemId: number, data: { parent_id?: number | null; category_id?: number | null }) => Promise<void>;
  categories?: Array<{ id: number; name: string }>;
}

const shuffle = <T,>(array: T[]): T[] => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export const CategoryPracticeSession: React.FC<CategoryPracticeSessionProps> = ({
  name,
  initialLeafCards,
  onClose,
  isInstanceOwner,
  onDelete,
  onMove,
  categories,
}) => {
  const [queue, setQueue] = useState<LeafCard[]>([]);
  const [currentCard, setCurrentCard] = useState<LeafCard | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const isDoneRef = useRef(false);

  const initSession = (cards: LeafCard[]) => {
    const shuffled = shuffle(cards);
    setTotalCount(shuffled.length);
    setCurrentCard(shuffled[0]);
    setQueue(shuffled.slice(1));
    setCompletedCount(0);
    setIsDone(false);
    isDoneRef.current = false;
    trackPracticeStarted('category');
  };

  useEffect(() => {
    if (initialLeafCards.length > 0) {
      initSession(initialLeafCards);
    }
  }, []);

  const handleCardDone = () => {
    setCompletedCount((c) => c + 1);
    if (queue.length > 0) {
      setCurrentCard(queue[0]);
      setQueue(queue.slice(1));
    } else {
      setIsDone(true);
      isDoneRef.current = true;
      trackPracticeCompleted('category');
    }
  };

  const handleSkip = () => {
    if (!currentCard) return;
    if (queue.length === 0) {
      // Last card — can't skip, just end
      setIsDone(true);
      return;
    }
    const [next, ...rest] = queue;
    setQueue([...rest, currentCard]);
    setCurrentCard(next);
  };

  if (initialLeafCards.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 flex flex-col items-center text-center">
          <Tag className="h-12 w-12 text-gray-300 mb-4" />
          <p className="text-lg font-medium text-gray-800 mb-2">No cards in this category</p>
          <p className="text-sm text-gray-500 mb-6">Add cards to this category to start a practice session.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (isDone) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Nice work!</h2>
          <p className="text-sm text-gray-500 mb-8">
            You practiced <span className="font-medium text-gray-700">{completedCount}</span> cards in{' '}
            <span className="font-medium text-gray-700">{name}</span>.
          </p>
          <div className="flex items-center gap-3 w-full">
            <button
              onClick={() => initSession(initialLeafCards)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Practice again
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentCard) return null;

  const position = completedCount + 1;

  return (
    <PracticeCard
      key={currentCard.id}
      cardId={currentCard.id}
      cardName={currentCard.name}
      onClose={() => { if (!isDoneRef.current) trackPracticeAbandoned('category'); onClose(); }}
      isInstanceOwner={isInstanceOwner}
      onDelete={onDelete}
      onMove={onMove}
      categories={categories}
      sessionInfo={{
        position,
        total: totalCount,
        onNext: handleCardDone,
      }}
    />
  );
};
